import { useState } from 'react';

import { Shield, Copy, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface IpWhitelistBannerProps {
  egressIps: string[];
  className?: string;
}

/**
 * Displays egress IPs that customers need to whitelist in their database firewall.
 * Includes click-to-copy functionality for each IP.
 */
export function IpWhitelistBanner({ egressIps, className }: IpWhitelistBannerProps) {
  const [copiedIp, setCopiedIp] = useState<string | null>(null);

  if (!egressIps || egressIps.length === 0) {
    return null;
  }

  const handleCopy = async (ip: string) => {
    try {
      await navigator.clipboard.writeText(ip);
      setCopiedIp(ip);
      setTimeout(() => setCopiedIp(null), 2000);
    } catch (err) {
      console.error('Failed to copy IP:', err);
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(egressIps.join('\n'));
      setCopiedIp('all');
      setTimeout(() => setCopiedIp(null), 2000);
    } catch (err) {
      console.error('Failed to copy IPs:', err);
    }
  };

  return (
    <div className={cn('rounded-md border border-border bg-muted/30 px-3 py-2.5', className)}>
      <div className="flex items-center gap-2 mb-2">
        <Shield className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          Whitelist {egressIps.length === 1 ? 'this IP' : 'these IPs'} in your database firewall:
        </span>
      </div>
      <div className="space-y-1.5">
        {egressIps.map((ip) => (
          <div
            key={ip}
            className="flex items-center justify-between gap-2 rounded bg-muted px-2.5 py-1.5"
          >
            <code className="font-mono text-xs font-medium">
              {ip}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => handleCopy(ip)}
            >
              {copiedIp === ip ? (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
        ))}
      </div>
      {egressIps.length > 1 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1.5 h-6 px-2 text-xs"
          onClick={handleCopyAll}
        >
          {copiedIp === 'all' ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              Copied All
            </>
          ) : (
            <>
              <Copy className="h-3 w-3 mr-1" />
              Copy All
            </>
          )}
        </Button>
      )}
    </div>
  );
}
